import React, { useState, useEffect, useMemo } from 'react';
import { X, Check, FileText, Printer, Presentation, FileSpreadsheet, ChevronRight, ChevronDown, Square, CheckSquare, MinusSquare } from 'lucide-react';
import { Section } from '../types';

export type ExportType = 'word' | 'pdf' | 'pptx' | 'xlsx';

interface ExportSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: Section[];
  projectTitle: string;
  exportType: ExportType;
  onExport: (selectedSections: Section[]) => void;
  isExporting?: boolean;
}

const EXPORT_CONFIG: Record<ExportType, { icon: React.ElementType; label: string; description: string; color: string }> = {
  word: { icon: FileText, label: 'Word Document', description: 'Formatted text with styling', color: 'text-blue-500' },
  pdf: { icon: Printer, label: 'PDF / Print', description: 'High-fidelity layout', color: 'text-gray-500' },
  pptx: { icon: Presentation, label: 'PowerPoint', description: 'AI-generated slides', color: 'text-orange-500' },
  xlsx: { icon: FileSpreadsheet, label: 'Excel', description: 'Data tables extraction', color: 'text-emerald-500' }
};

export const ExportSelectionModal: React.FC<ExportSelectionModalProps> = ({
  isOpen,
  onClose,
  sections,
  projectTitle,
  exportType,
  onExport,
  isExporting = false
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const sortedSections = useMemo(() => 
    [...sections].sort((a, b) => a.order - b.order),
    [sections]
  );

  const rootSections = useMemo(() => 
    sortedSections.filter(s => !s.parentId),
    [sortedSections]
  );

  const getChildren = (parentId: string) => 
    sortedSections.filter(s => s.parentId === parentId);

  const getAllDescendantIds = (sectionId: string): string[] => {
    const children = getChildren(sectionId);
    const descendantIds: string[] = [];
    for (const child of children) {
      descendantIds.push(child.id);
      descendantIds.push(...getAllDescendantIds(child.id));
    }
    return descendantIds;
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(sections.map(s => s.id)));
      setExpandedIds(new Set(rootSections.map(s => s.id)));
    }
  }, [isOpen, sections, rootSections]);

  const handleToggleSection = (sectionId: string) => {
    const newSelected = new Set(selectedIds);
    const descendants = getAllDescendantIds(sectionId);
    
    if (newSelected.has(sectionId)) {
      newSelected.delete(sectionId);
      descendants.forEach(id => newSelected.delete(id));
    } else {
      newSelected.add(sectionId);
      descendants.forEach(id => newSelected.add(id));
    }
    
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    setSelectedIds(new Set(sections.map(s => s.id)));
  };

  const handleClearAll = () => {
    setSelectedIds(new Set());
  };

  const handleToggleExpand = (sectionId: string) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedIds(newExpanded);
  };

  const handleExport = () => {
    const selectedSections = sortedSections.filter(s => selectedIds.has(s.id));
    onExport(selectedSections);
  };

  const getCheckboxState = (sectionId: string): 'checked' | 'unchecked' | 'partial' => {
    const isSelected = selectedIds.has(sectionId);
    const children = getChildren(sectionId);
    
    if (children.length === 0) {
      return isSelected ? 'checked' : 'unchecked';
    }
    
    const allChildrenSelected = children.every(c => selectedIds.has(c.id));
    const someChildrenSelected = children.some(c => selectedIds.has(c.id));
    
    if (isSelected && allChildrenSelected) return 'checked';
    if (someChildrenSelected || isSelected) return 'partial';
    return 'unchecked';
  };

  const renderSection = (section: Section, depth: number = 0) => {
    const children = getChildren(section.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedIds.has(section.id);
    const checkState = getCheckboxState(section.id);
    
    const CheckIcon = checkState === 'checked' ? CheckSquare : 
                      checkState === 'partial' ? MinusSquare : Square;
    
    return (
      <div key={section.id}>
        <div 
          className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-cc-tile-hover cursor-pointer transition-colors ${
            depth > 0 ? 'ml-6' : ''
          }`}
          onClick={() => handleToggleSection(section.id)}
        >
          {hasChildren && (
            <button
              onClick={(e) => { e.stopPropagation(); handleToggleExpand(section.id); }}
              className="p-0.5 hover:bg-cc-surface rounded"
            >
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
          {!hasChildren && <div className="w-5" />}
          
          <CheckIcon 
            size={18} 
            className={`flex-shrink-0 ${
              checkState === 'checked' ? 'text-cc-accent' : 
              checkState === 'partial' ? 'text-cc-accent/60' : 
              'text-cc-text-muted'
            }`}
          />
          
          <span className={`flex-1 text-sm ${selectedIds.has(section.id) ? 'text-cc-text' : 'text-cc-text-muted'}`}>
            {section.title}
          </span>
          
          {section.content && (
            <span className="text-[10px] text-cc-text-muted bg-cc-surface px-1.5 py-0.5 rounded">
              {section.content.replace(/<[^>]*>/g, '').length} chars
            </span>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="border-l border-cc-border ml-5">
            {children.map(child => renderSection(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const config = EXPORT_CONFIG[exportType];
  const ExportIcon = config.icon;
  const selectedCount = selectedIds.size;
  const totalCount = sections.length;

  return (
    <>
      <div 
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[201] flex items-center justify-center p-4 pointer-events-none">
        <div 
          className="bg-cc-elevated rounded-2xl shadow-2xl border border-cc-border w-full max-w-lg max-h-[80vh] flex flex-col pointer-events-auto animate-in zoom-in-95 fade-in duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-cc-border">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-cc-surface ${config.color}`}>
                <ExportIcon size={20} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-cc-text">Export to {config.label}</h2>
                <p className="text-xs text-cc-text-muted">{projectTitle}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-cc-tile-hover text-cc-text-muted transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-4 py-3 border-b border-cc-border bg-cc-surface/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSelectAll}
                  className="text-xs font-medium text-cc-accent hover:underline"
                >
                  Select All
                </button>
                <span className="text-cc-text-muted">|</span>
                <button
                  onClick={handleClearAll}
                  className="text-xs font-medium text-cc-text-muted hover:text-cc-text"
                >
                  Clear
                </button>
              </div>
              <div className="text-xs text-cc-text-muted">
                <span className="font-semibold text-cc-text">{selectedCount}</span> of {totalCount} sections
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
            {rootSections.length > 0 ? (
              rootSections.map(section => renderSection(section))
            ) : (
              <div className="text-center py-8 text-cc-text-muted text-sm">
                No sections available
              </div>
            )}
          </div>

          <div className="p-4 border-t border-cc-border flex items-center justify-between gap-3">
            <p className="text-xs text-cc-text-muted">
              {exportType === 'pptx' || exportType === 'xlsx' 
                ? 'AI will generate content from selected sections'
                : 'Selected sections will be included in export'
              }
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-cc-text-muted hover:bg-cc-tile-hover rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleExport}
                disabled={selectedCount === 0 || isExporting}
                className="px-4 py-2 text-sm font-semibold bg-cc-accent text-cc-accent-text rounded-lg hover:bg-cc-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isExporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-cc-accent-text/30 border-t-cc-accent-text rounded-full animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Check size={16} />
                    Export ({selectedCount})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
